import React from "react";

import { Option, Select } from "src/base-components/Select";
import { OrganizationUser } from "src/clients/taktile-api";
import { Avatar } from "src/design-system/Avatar";
import { Icon } from "src/design-system/Icon";
import { getUserName } from "src/utils/user";

type UserOption = {
  key: string;
  user: OrganizationUser;
};

export type AssigneeSelectOption = UserOption | Option;

const isUserOption = (option: AssigneeSelectOption): option is UserOption =>
  "user" in option;

type AssigneeSelectProps = {
  options: AssigneeSelectOption[];
  value: Nullable<string> | undefined;
  onChange: (value: string) => void;
  dataLoc?: string;
  disabled?: boolean;
  loading?: boolean;
};

const renderOption = (option: AssigneeSelectOption): Option => {
  if (isUserOption(option)) {
    const name = getUserName(option.user) ?? "Unknown user";
    return {
      key: option.key,
      searchText: name,
      value: (
        <span className="flex items-center gap-x-2">
          <Avatar user={option.user} />
          {name}
        </span>
      ),
    };
  }

  if ("icon" in option && option.icon) {
    return {
      key: option.key,
      searchText: typeof option.value === "string" ? option.value : undefined,
      value: (
        <span className="-ml-1 flex h-5 items-center gap-x-1.5">
          <Icon icon={option.icon} size="md" />
          {option.value}
        </span>
      ),
    };
  }

  return option;
};

export const AssigneeSelect: React.FC<AssigneeSelectProps> = ({
  options,
  value,
  onChange,
  dataLoc,
  disabled,
  loading,
}) => (
  <Select
    dataLoc={dataLoc}
    disabled={disabled}
    loading={loading}
    options={options.map(renderOption)}
    placeholder="Select assignee"
    searchPlaceholder="Search assignee..."
    value={value}
    searchable
    fullWidth
    onChange={onChange}
  />
);
